import { intakeHref } from '../lib/referral';
import React, { useState, useEffect } from 'react';
import { Menu, X, ArrowRight } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { nav } from '../mock';

export default function Header({ hideCta = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  // Auto-hide the "Check Eligibility" CTA on quiz-funnel pages so the patient
  // doesn't accidentally restart the flow.
  const funnel = /\/(quiz|qualifying|results|checkout)/.test(pathname);
  const showCta = !hideCta && !funnel;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll while mobile menu is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const closeMenu = () => setOpen(false);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${
          scrolled || open ? 'bg-[color:var(--alfa-cream)]/90 backdrop-blur-md border-b border-neutral-900/5' : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1360px] mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-3">
          {/* Mobile: hamburger LEFT — no border, just 3 lines */}
          <button
            type="button"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="md:hidden inline-flex items-center justify-center w-10 h-10 -ml-2 text-neutral-900"
          >
            {open ? <X size={26} strokeWidth={2} /> : <Menu size={26} strokeWidth={2} />}
          </button>

          {/* Logo: LEFT on desktop, CENTER on mobile (via absolute centering) */}
          <a href="/" className="flex items-center shrink-0 md:static absolute left-1/2 -translate-x-1/2 md:translate-x-0">
            <img
              src="/images/alfa-logo-black.png?v=2"
              alt="ALFÀ"
              className="h-9 sm:h-8 md:h-9 w-auto object-contain"
            />
          </a>

          <nav className="hidden md:flex items-center gap-8 lg:gap-12">
            {nav.map((n) => (
              <a
                key={n.label}
                href={n.href}
                data-testid={`nav-${n.label.toLowerCase().replace(/ /g, '-')}`}
                className="nav-link text-[15px] text-[color:var(--alfa-ink)]"
              >
                {n.label}
              </a>
            ))}
          </nav>

          {/* Desktop CTA */}
          {showCta && (
            <div className="hidden md:flex items-center gap-8">
              <span className="h-6 w-px bg-neutral-900/15" />
              <a
                href="https://portal.alfameds.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="header-patient-portal-link"
                className="nav-link text-[15px] text-[color:var(--alfa-ink)] whitespace-nowrap"
              >
                Patient portal
              </a>
              <a
                href={intakeHref()} target="_blank" rel="noopener noreferrer"
                data-testid="header-get-started-btn"
                className="btn-primary inline-flex items-center gap-2.5 text-[15px] px-6 py-3 rounded-md whitespace-nowrap"
              >
                Get started <ArrowRight size={16} />
              </a>
            </div>
          )}

          {/* Mobile right-side spacer to balance the hamburger — keeps logo visually centered */}
          <span className="md:hidden inline-block w-10 h-10" aria-hidden="true" />
        </div>
      </header>

      {/* Mobile menu drawer - moved outside header to allow full viewport coverage */}
      <div
        className={`md:hidden fixed inset-0 z-50 transition-opacity duration-300 ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ backgroundColor: '#fbf7f1' }}
      >
        {/* Solid mesh gradient background matching the site aesthetic */}
        <div
          className="absolute inset-0"
          style={{
            background:
              '#fbf7f1',
          }}
          onClick={closeMenu}
        />
        <div className="relative h-full flex flex-col px-6 pt-24 pb-10 overflow-y-auto">
          {/* Close button top-right, inside drawer */}
          <button
            type="button"
            aria-label="Close menu"
            onClick={closeMenu}
            className="absolute top-6 right-4 inline-flex items-center justify-center w-11 h-11 rounded-md border border-neutral-900/15 bg-white/80 text-neutral-900"
          >
            <X size={22} />
          </button>

          <nav className="flex flex-col gap-1">
            {nav.map((n, i) => (
              <a
                key={n.label}
                href={n.href}
                onClick={closeMenu}
                className="group flex items-center justify-between border-b border-neutral-900/10 py-5 text-[26px] tracking-[-0.02em] text-[color:var(--alfa-ink)]"
                style={{ transitionDelay: `${i * 40}ms` }}
              >
                {n.label}
                <ArrowRight size={20} className="text-neutral-400 group-hover:text-neutral-900 transition-colors" />
              </a>
            ))}
          </nav>

          {showCta && (
            <div className="mt-auto pt-8">
              <a
                href={intakeHref()} target="_blank" rel="noopener noreferrer"
                onClick={closeMenu}
                data-testid="mobile-get-started-btn"
                className="btn-primary flex items-center justify-center gap-2 text-[16px] px-6 py-4 rounded-md w-full"
              >
                Get started <ArrowRight size={16} />
              </a>
              <a
                href="https://portal.alfameds.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMenu}
                data-testid="mobile-patient-portal-link"
                className="mt-3 flex items-center justify-center px-6 py-4 rounded-md w-full border border-neutral-900/20 text-[16px] text-[color:var(--alfa-ink)]"
              >
                Patient portal
              </a>
              <p className="mt-4 text-center text-[13.5px] text-neutral-950">
                Licensed U.S. doctors &middot; No insurance needed
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
